import { Component } from '@angular/core';
import { Action, NavigationDataKey, UserPermissions } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { IFormGroup } from '@shared-libs/interfaces';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IEmployee } from '@internal-models/employee.model';
import { EmployeeProvider } from '@internal-providers/employee.provider';
import { EmployeeFormFactory } from '@internal-factories/forms/employee-form.factory';
import { InternalUserProvider } from '@internal-providers/user.provider';
import { MessageService } from '@shared-services/message.service';
import { IDigibankAgency } from '@internal-models/digibank-agency.model';
import { DigibankAgencyProvider } from '@internal-providers/digibank-agency.provider';
import { ValidationService } from '@shared-services/validation.service';
import { ActivatedRoute } from '@angular/router';
import { IUserForm, UserFormFactory } from '@internal-factories/forms/user-form.factory';

@Component({
	selector: 'employee-desktop-page',
	templateUrl: 'employee.desktop.page.html',
	styleUrls: ['./employee.desktop.page.scss'],
})
export class EmployeeDesktopPage {
	public action: Action = Action.CREATE;
	public employee: IEmployee;
	public employeeId: string;
	public employeeForm: IFormGroup<IEmployee>;
	public userForm: IFormGroup<IUserForm>;
	public agencies: Array<IDigibankAgency>;
	public wantsBeegoCommunicationOptions: Array<{ value: boolean; label: string }>;

	public readonly Action: typeof Action = Action;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly navigationService: NavigationService,
		private readonly provider: EmployeeProvider,
		private readonly userProvider: InternalUserProvider,
		private readonly messageService: MessageService,
		private readonly digibankAgencyProvider: DigibankAgencyProvider,
		private readonly validationService: ValidationService,
		private readonly route: ActivatedRoute
	) {
		this.route.params.subscribe(async (params) => {
			this.employee = this.navigationService.getNavigationData(NavigationDataKey.employee);
			this.employeeId = params?.employeeId || this.employee?.id;
			if (this.employeeId) {
				this.employee = await this.provider.getOne(this.employeeId);
			}
			this.action =
				this.navigationService.getNavigationData(NavigationDataKey.action) ??
				(!!this.employee ? Action.UPDATE : Action.CREATE);
			this.initializeForm();
			void this.getDigibankAgencies();
			this.getWantsBeegoCommunicationOptions();
		});
	}

	public async createEmployee(): Promise<void> {
		if (formIsValid(this.employeeForm, EmployeeDesktopPage)) {
			const employee = { ...this.employeeForm.value, User: UserFormFactory.getValue(this.userForm) };
			this.employee = await this.provider.create(employee);
			this.messageService.showSavedMessage(translate('EMPLOYEE.CREATED'));
			this.navigationService.navigateBack({ action: Action.CREATE });
		}
	}

	public async updateEmployee(): Promise<void> {
		if (formIsValid(this.employeeForm, EmployeeDesktopPage)) {
			const employeeForm = this.employeeForm.value;
			await this.provider.update(employeeForm.id, employeeForm);
			this.messageService.showSavedMessage();
		}
	}

	/**
	 * Resend the invitation mail
	 * @param userId the userId to sent the invitation to
	 */
	public async resendInvite(userId: string): Promise<void> {
		await this.userProvider.resendInvite(userId);
		this.messageService.showSavedMessage(translate('USERS.RE_INVITE_COMPLETED'));
	}

	/**
	 * Disable a user
	 * @param userId the userId to disable
	 */
	public async disableUser(userId: string): Promise<void> {
		await this.userProvider.disableUser(userId);
		this.employee.User.isDisabled = true;
		this.employee.User.isActive = false;
		this.messageService.showSavedMessage(translate('USERS.DISABLED_COMPLETED'));
	}

	/**
	 * Re-enable a user
	 * @param userId the userId to re-enable
	 */
	public async enableUser(userId: string): Promise<void> {
		await this.userProvider.enableUser(userId);
		this.employee.User.isDisabled = false;
		this.employee.User.isActive = true;
		this.messageService.showSavedMessage(translate('USERS.ENABLED_COMPLETED'));
	}

	/**
	 * Delete an employee (from the database)
	 */
	public async deleteEmployee(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('EMPLOYEE')) {
			await this.provider.delete(this.employee.id);
			this.navigationService.navigateBack({ action: Action.DELETE });
		}
	}

	/**
	 * Initialize the form for create or update
	 */
	private async initializeForm(): Promise<void> {
		this.employeeForm =
			this.action === Action.CREATE
				? EmployeeFormFactory.createForCreate()
				: EmployeeFormFactory.createForUpdate(this.employee);
		this.userForm = UserFormFactory.create(this.employee?.User);
	}

	/**
	 * Get the digibank agencies from the API
	 */
	private async getDigibankAgencies(): Promise<void> {
		this.agencies = await this.digibankAgencyProvider.getAll();
	}

	/**
	 * Get the wantsBeegoCommunicationOptions options
	 * formatted for the form-select
	 */
	private getWantsBeegoCommunicationOptions(): void {
		this.wantsBeegoCommunicationOptions = [
			{ value: true, label: translate('YES') },
			{ value: false, label: translate('NO') },
		];
	}
}
